'use client';

import type { ReactNode } from 'react';

import { useMemo, useCallback } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { RADIUS } from 'src/theme/ui-tokens';
import { AI_CHAT_VISIBLE } from 'src/constants/ai-chat-visibility';

import { Iconify } from 'src/components/iconify';
import PostCard from 'src/components/cmui/post-card/PostCard';
import PostTable from 'src/components/cmui/post-card/PostTable';
import { PostThumb } from 'src/components/cmui/post-card/PostThumb';
import PostCardList from 'src/components/cmui/post-card/PostCardList';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';

import { toBookmarkTarget } from '../utils/search-all-media-cards';
import { postIdOf, cardKeysOfRows } from '../utils/search-all-media-order';
import { MEDIA_EMPHASIS_SX, MEDIA_LIST_EMPHASIS_SX } from '../constants/search-all-media';

import type { MediaCard } from '../utils/search-all-media-cards';
import type { DetailHighlight } from '../utils/search-all-detail-options';

/**
 * 섬네일 말고 **다른 세 보기** — 카드 · 리스트 · 테이블.
 *
 * 🔑 넷 다 **같은 것을 준다** — 누르면 같은 상세 다이얼로그가 열리고(`onOpen`), 칸마다 북마크가 붙는다.
 *   다른 것은 **한 칸에 무엇까지 보이나**뿐이다(실물만 · 본문까지 · 한 줄로 · 수치까지).
 * ⚠️ **섬네일은 여기 없다** — 그것은 실물을 줄마다 꽉 채워 깔고 영상까지 트는 `media-grid` 가 그린다.
 *   여기 셋은 훑는 보기가 아니라 **읽는 보기**라 대표 이미지만 세운다.
 */

export type MediaViewProps = {
  cards: MediaCard[];
  onOpen: (card: MediaCard) => void;
  /** 읽기만 하는 자리인가 — 북마크·담기를 감춘다 */
  readOnly?: boolean;
  /** AI 에게 물을 소재 고르기 — 없으면 담기 단추를 두지 않는다 */
  selectedKeys?: Set<string>;
  selectFull?: boolean;
  onToggleSelect?: (card: MediaCard) => void;
  /** 화면이 지금 편 순서(카드 key)가 바뀔 때 — 테이블만 부른다(제 정렬·페이지로 순서가 달라서다) */
  onOrderChange?: (keys: string[]) => void;
  /**
   * 부르는 화면이 칸마다 얹는 동작 — 카드 보기는 칸 위에 겹쳐(`'overlay'`), 리스트·테이블은 줄 동작 자리에(`'inline'`).
   * ⚠️ 없으면 아무것도 안 그린다(다른 화면은 그대로다).
   */
  cardActions?: (card: MediaCard, place: 'overlay' | 'inline' | 'row') => ReactNode;
  /**
   * 본문에 칠할 말 — 글 카드·글 줄·표가 제 형광 칸(`searchKeyword`·`clientSearch`·`topicKeywords`)으로 받는다.
   * ⚠️ 표에는 `searchKeyword` 만 준다 — 표는 `clientSearch`·`topicKeywords` 로 **줄을 거른다**(칠하기가 아니다).
   */
  highlight?: DetailHighlight | null;
  /** 테를 둘러 강조할 카드 key — 카드·리스트는 테, 표는 `searchKeyword` 가 든 줄에 제 바탕을 깐다 */
  emphasizedKeys?: ReadonlySet<string>;
};

/**
 * 카드 — 본문까지 읽는 보기. 글 카드를 여러 칸으로 깐다.
 *
 * 🔑 칸은 **공용 `PostCard`** 다(검색·키워드 모니터링·브랜드 멘션이 쓰는 그 카드) — 인용·리포스트 상자,
 *   검색어 형광, 반응 수치, 북마크가 전부 딸려 온다. 모양을 새로 그리지 않는다.
 * 🔑 **세로로 흘려 담는다**(CSS `columns`) — 격자로 깔면 본문 길이가 제각각이라 한 줄에서 가장 긴 칸이
 *   높이를 정하고 나머지 자리가 통째로 빈다.
 * ⚠️ 글 원본이 없는 카드(광고·상품)는 그 카드를 못 그린다 — 그때는 섬네일 칸으로 떨어진다.
 * ⚠️ 글 카드에는 **이름을 덧붙이지 않는다** — 카드가 머리에 이미 그린다(덧붙이면 같은 이름이 두 번 적힌다).
 */
export function MediaCardView({ cards, onOpen, readOnly, selectedKeys, selectFull, onToggleSelect, cardActions, highlight, emphasizedKeys }: MediaViewProps) {
  return (
    <Box
      sx={{
        columnWidth: 300,
        // ⚠️ `columnGap: 16` 은 **128px** 이 된다(테마 간격 ×8) — 칸이 둘로 줄어든다. 간격은 배수로 적는다
        columnGap: 2,
        '& > *': { breakInside: 'avoid', mb: 2 },
      }}
    >
      {cards.map((card) => (
        <Box key={card.key} sx={{ position: 'relative', display: 'inline-block', width: 1, ...(emphasizedKeys?.has(card.key) ? { ...MEDIA_EMPHASIS_SX, borderRadius: RADIUS.lg } : null) }}>
          {/*
            ⚠️ 글 카드의 **제 북마크는 끈다** — 그 부품은 「북마크」 글자 칩을 그리는데 광고·상품
              카드는 아이콘을 쓴다. 한 격자에 두 꼴이 섞이면 같은 일로 안 읽힌다(리스트와 같은 규약).
          */}
          {card.post ? (
            <PostCard
              post={card.post}
              onClick={() => onOpen(card)}
              showBookmarkButton={false}
              searchKeyword={highlight?.searchKeyword ?? ''}
              clientSearch={highlight?.clientSearch ?? ''}
              topicKeywords={highlight?.topicKeywords ?? NO_TERMS}
              actionSlot={
                <>
                  <Pick card={card} selectedKeys={selectedKeys} selectFull={selectFull} onToggleSelect={onToggleSelect} />
                  <Bookmark card={card} readOnly={readOnly} />
                </>
              }
            />
          ) : (
            /*
              🚨 광고·상품도 **글 카드와 같은 틀**에 담는다 — 틀 있는 카드와 맨 그림을 섞으면 한 격자
                안에 두 물건이 선다. 틀은 디자인 시스템의 카드 그대로다(흰 면 · `divider` 테두리 ·
                `RADIUS.lg` · 그림자 없음).
            */
            <Box sx={{ borderRadius: RADIUS.lg, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
              <Box onClick={() => onOpen(card)} sx={{ cursor: 'pointer' }}>
                <PostThumb src={card.thumb} alt={card.name} text={card.title} isVideo={card.video} maxHeight={320} radius={0} />
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1.5, py: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ flex: 1, minWidth: 0 }} variant="uiStrong">{card.name}</Typography>
                <Pick card={card} selectedKeys={selectedKeys} selectFull={selectFull} onToggleSelect={onToggleSelect} />
                <Bookmark card={card} readOnly={readOnly} />
              </Stack>
            </Box>
          )}
          {cardActions?.(card, 'overlay')}
        </Box>
      ))}
    </Box>
  );
}

/**
 * 리스트 — 한 줄에 하나. 본문까지 읽는 보기.
 *
 * 🚨 **줄은 한 꼴이다** — 글·광고·상품이 섞여 오므로 셋이 같은 뼈대로 서야 한 목록으로 읽힌다.
 *   테두리를 두른 상자와 맨 줄을 섞으면 같은 목록 안에 두 물건이 선다(실측: 상자 61px · 맨 줄 206px).
 *   그래서 **테두리 없이 맨 줄**로 세우고 **구분선 하나**로 가른다.
 * 🔑 글 줄은 **공용 `PostCardList`** 다(키워드 모니터링·브랜드 멘션이 쓰는 그 줄) — 모양을 새로 그리지 않는다.
 *   광고·상품은 글 원본이 없어 그 줄을 못 쓰므로, **같은 뼈대**(왼쪽 정사각 컷 · 이름 · 딸린 말 · 수치)로 맞춘다.
 * 🔑 담기(`Pick`)와 북마크는 **줄 안의 한 묶음**이다 — 같은 상자(22×22 알약)를 쓰고 색이 상태를 진다.
 * ⚠️ **원문 열기 단추는 두지 않는다** — 줄을 누르면 상세가 열리고 거기 발에 원문이 있다.
 *   네 보기 중 리스트에만 있던 단추라, 보기를 바꾸면 없어지는 일이 됐다.
 */
export function MediaListView({ cards, onOpen, readOnly, selectedKeys, selectFull, onToggleSelect, cardActions, highlight, emphasizedKeys }: MediaViewProps) {
  return (
    <Stack divider={<Divider sx={{ borderColor: 'divider' }} />}>
      {cards.map((card) => (
        <Box key={card.key} sx={{ position: 'relative', ...(emphasizedKeys?.has(card.key) ? { ...MEDIA_LIST_EMPHASIS_SX, borderRadius: RADIUS.md } : null) }}>
          {/*
            🔑 동작은 **줄 안에서** 낸다 — 글 줄 부품은 제 배경을 가지므로, 밖에 덧대면 줄 끝에
              이음매가 생기고 아이콘만 먼 오른쪽에 떠 있게 된다(실측 이음매 987px 대 939px).
          */}
          {card.post ? (
            <PostCardList
              post={card.post}
              onClick={() => onOpen(card)}
              showBookmarkButton={!readOnly}
              searchKeyword={highlight?.searchKeyword ?? ''}
              clientSearch={highlight?.clientSearch ?? ''}
              topicKeywords={highlight?.topicKeywords ?? NO_TERMS}
              actionSlot={
                <>
                  <Pick card={card} selectedKeys={selectedKeys} selectFull={selectFull} onToggleSelect={onToggleSelect} />
                  {cardActions?.(card, 'inline')}
                </>
              }
            />
          ) : (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.5 }}>
              {/*
                ⚠️ 컷은 **정사각으로 자른다** — 세로로 긴 컷이 오면 줄 높이가 그 컷에 끌려가
                  같은 목록에서 줄마다 높이가 달라진다(실측 95 · 152 · 206px).
              */}
              <Box
                onClick={() => onOpen(card)}
                sx={{ width: 72, height: 72, flexShrink: 0, cursor: 'pointer', borderRadius: RADIUS.sm, overflow: 'hidden' }}
              >
                <PostThumb src={card.thumb} alt={card.name} isVideo={card.video} maxHeight={72} radius={0} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap variant="uiStrong">{card.name}</Typography>
                {/* ⚠️ 광고는 이름과 제목이 같이 오는 일이 있다 — 같은 말을 두 줄로 적지 않는다 */}
                {card.title && card.title !== card.name && (
                  <Typography variant="uiBody" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {card.title}
                  </Typography>
                )}
                <Stats stats={card.stats} sx={{ mt: 1 }} />
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                <Pick card={card} selectedKeys={selectedKeys} selectFull={selectFull} onToggleSelect={onToggleSelect} />
                <Bookmark card={card} readOnly={readOnly} />
                {cardActions?.(card, 'inline')}
              </Stack>
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
}

/**
 * 테이블 — 칸으로 나눈 줄. 수치를 견주는 보기.
 * 🔑 표는 **공용 `PostTable`** 이다 — 정렬·지표 열·북마크 열이 그 부품의 계약 그대로 따라온다.
 * 🔑 담기(`Pick`)도 **네 보기 모두에 있다** — 표에만 없으면 「표로 보면 담을 수 없다」가 된다.
 *   표의 줄 동작 자리(`renderRowAction`)에 북마크와 나란히 세운다.
 * ⚠️ 글 원본이 없는 카드(광고·상품)는 표에 서지 못한다 — 그 수를 한 줄로 알린다.
 * 🔑 처음 순서는 받은 카드 순서(화면의 정렬 고르개)다 — `defaultSortField` 를 비운다.
 * 🚨 **표의 제 상세(`PostDetail`)는 끈다**(`ownDetail={false}`) — 켜 두면 줄을 누를 때 다른 꼴의 상세가
 *   소재 상세 위에 겹쳐 열린다. 표는 줄을 누른 사실과 편 순서(`onOrderChange`)만 올려 보낸다.
 */
export function MediaTableView({ cards, onOpen, readOnly, selectedKeys, selectFull, onToggleSelect, onOrderChange, cardActions, highlight }: MediaViewProps) {
  // 🔑 줄 목록을 붙잡아 둔다 — 새 배열을 넘기면 표가 순서를 매번 다시 올려 보낸다
  const posts = useMemo(() => cards.map((card) => card.post).filter((post): post is Record<string, unknown> => Boolean(post)), [cards]);
  const missing = cards.length - posts.length;
  const byId = useMemo(() => new Map(cards.filter((card) => card.post).map((card) => [postIdOf(card.post), card])), [cards]);

  const handleRows = useCallback(
    (rows: readonly Record<string, unknown>[]) => onOrderChange?.(cardKeysOfRows(cards, rows)),
    [cards, onOrderChange],
  );

  return (
    <>
      <PostTable
        posts={posts}
        ownDetail={false}
        defaultSortField=""
        onRowsChange={handleRows}
        searchKeyword={highlight?.searchKeyword ?? ''}
        onClickPost={(post: Record<string, unknown>) => {
          const card = byId.get(postIdOf(post));
          if (card) onOpen(card);
        }}
        enableImageBookmark={!readOnly}
        renderRowAction={(post: Record<string, unknown>) => {
          const card = byId.get(postIdOf(post));
          if (!card) return null;
          return (
            <>
              <Pick card={card} selectedKeys={selectedKeys} selectFull={selectFull} onToggleSelect={onToggleSelect} />
              {cardActions?.(card, 'row')}
            </>
          );
        }}
      />
      {missing > 0 && (
        <Typography sx={{ mt: 1, typography: 'uiMeta', color: 'text.disabled' }}>
          광고·상품 {missing}건은 표로 펴지 않습니다 — 카드나 섬네일 보기로 보세요.
        </Typography>
      )}
    </>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

const NO_TERMS: readonly string[] = [];

/** 지표 줄 — 카드가 준 것을 그대로 편다(채널마다 주는 값이 다르다) */
function Stats({ stats, sx }: { stats: MediaCard['stats']; sx?: object }) {
  if (stats.length === 0) return null;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={sx}>
      {stats.map((stat) => (
        <Stack key={stat.icon} direction="row" spacing={0.25} alignItems="center">
          <Iconify icon={STAT_ICON[stat.icon]} width={12} sx={{ color: 'text.disabled' }} />
          <Typography sx={{ typography: 'uiMeta', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{stat.value}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

const STAT_ICON: Record<MediaCard['stats'][number]['icon'], string> = {
  views: 'solar:play-linear',
  likes: 'solar:heart-linear',
  comments: 'solar:chat-round-dots-linear',
  rating: 'solar:star-linear',
  reviews: 'solar:chat-square-like-linear',
};

/**
 * AI 에게 물을 소재로 담기 — 담긴 것은 색이 차고, 상한에 닿으면 새로 담기지 않는다.
 * ⚠️ 고르기 값을 안 받은 자리, AI 스위치(`AI_CHAT_VISIBLE`)가 꺼진 동안에는 단추를 두지 않는다.
 */
function Pick({
  card,
  selectedKeys,
  selectFull,
  onToggleSelect,
}: {
  card: MediaCard;
  selectedKeys?: Set<string>;
  selectFull?: boolean;
  onToggleSelect?: (card: MediaCard) => void;
}) {
  if (!AI_CHAT_VISIBLE || !onToggleSelect || !selectedKeys) return null;
  const on = selectedKeys.has(card.key);
  return (
    <Tooltip
      title={on ? 'AI 에서 빼기' : selectFull ? '더 담을 수 없습니다' : 'AI 에게 묻기'}
      placement="top"
      disableInteractive
    >
      <Box
        component="button"
        type="button"
        disabled={!on && Boolean(selectFull)}
        onClick={(event) => {
          event.stopPropagation();
          onToggleSelect(card);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          p: 0,
          border: 0,
          borderRadius: RADIUS.pill,
          cursor: 'pointer',
          color: on ? 'primary.contrastText' : 'text.disabled',
          bgcolor: on ? 'primary.main' : 'transparent',
          '&:disabled': { opacity: 0.4, cursor: 'default' },
          '&:hover': { color: on ? 'primary.contrastText' : 'text.primary' },
        }}
      >
        {/* ⚠️ 화면 아래 안내가 「카드의 **+** 로 소재를 담거나」라고 말한다 — 아이콘이 그 말과 같아야 한다 */}
        <Iconify icon={on ? 'solar:check-circle-linear' : 'solar:add-circle-linear'} width={14} />
      </Box>
    </Tooltip>
  );
}

/** 북마크 — 광고는 광고 원장 열쇠로 담는다(카드와 같은 규약) */
/**
 * 담기 단추 — **담을 대상이 만들어지면 둔다**(`toBookmarkTarget`).
 *
 * 🚨 갈래를 손으로 세지 마라 — 「글이거나 광고일 때만」으로 가려 두었더니 **판매몰 상품에만 단추가
 *   없었다**. 북마크는 상품도 담을 수 있고(문서의 `source` 가 판매몰 코드다) 북마크 폴더에 상품
 *   목록까지 있는데, 이 화면에서만 담을 길이 없었다.
 * ⚠️ 가리는 자리는 **한 곳**(`toBookmarkTarget`)이다 — 여기서 또 가리면 둘이 어긋난다.
 */
function Bookmark({ card, readOnly, sx }: { card: MediaCard; readOnly?: boolean; sx?: object }) {
  const target = readOnly ? null : toBookmarkTarget(card);
  if (!target) return null;
  return (
    <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'flex', ...sx }}>
      <PostCardBookmarkOverlay post={target} placement="postTable" />
    </Box>
  );
}

/** 원문 열기 — 주소가 없으면 두지 않는다 */
function Original({ href }: { href: string }) {
  if (!href) return null;
  return (
    <Tooltip title="원문 열기" placement="top" disableInteractive>
      <Link
        href={href}
        target="_blank"
        rel="noopener"
        onClick={(event) => event.stopPropagation()}
        sx={{ display: 'flex', color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
      >
        <Iconify icon="solar:square-top-down-linear" width={15} />
      </Link>
    </Tooltip>
  );
}
